import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import parse from 'html-react-parser';
import {colors, shadows, borderRadius, fontSize, fontWeight, letterSpacing} from '@/styles/tokens';

// ---------- sx ----------
const mainCardSx = {
    bgcolor: `${colors.background.subtle} !important`,
    border: `1px solid ${colors.neutral.grayLight}`,
    borderRadius: borderRadius.lg,
    p: {xs: 3, md: 6},
    boxShadow: shadows.lg
} as const;

const impactHeaderSx = {
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    mb: 3
} as const;

const impactValueSx = {
    fontWeight: fontWeight.extrabold,
    color: colors.primary.navy,
    mb: 1,
    fontSize: fontSize[40]
} as const;

const impactSubtextSx = {
    mb: 4,
    fontSize: fontSize[16],
    color: colors.text.primary
} as const;

const accordionSx = {
    bgcolor: `${colors.background.subtle} !important`,
    boxShadow: 'none',
    border: 'none',
    borderTop: `1px solid ${colors.neutral.grayLight}`,
    borderRadius: '0 !important',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
    '&.MuiPaper-root': {
        borderLeft: 'none !important',
        borderRight: 'none !important',
        boxShadow: 'none !important'
    }
} as const;

const accordionSummarySx = {
    px: 0,
    py: 1,
    '& .MuiAccordionSummary-content': {
        fontWeight: fontWeight.bold,
        color: colors.text.primary
    }
} as const;

const bulletSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    mb: 1.5
} as const;

const bulletPointSx = {
    width: 8,
    height: 8,
    flexShrink: 0,
    bgcolor: colors.primary.navy,
    borderRadius: '0%',
    fontSize: fontSize[16]
} as const;

const cellSx = {fontSize: fontSize[14], textAlign: 'left' as const};
const firstCellSx = {fontSize: fontSize[14], textAlign: 'left' as const, pr: 1};

// ---------- accordion content renderers ----------
const renderBullets = (items: any) =>
    items?.map((item: any, idx: any) => (
        <Box key={idx} sx={bulletSx}>
            <Box sx={bulletPointSx} />
            <Typography>{item.title ? parse(item.title) : ''}</Typography>
        </Box>
    ));

const hasIntegrationCost = (items: any[]) => items?.some((item) => item.integrationCost);

const renderProjectTable = (items: any) => {
    const showIntegration = hasIntegrationCost(items);
    const gridCols = showIntegration ? '2fr 1fr 1fr 0.7fr' : '2fr 1fr 0.7fr';

    return (
        <Box sx={{overflowX: 'auto'}}>
            <Box sx={{minWidth: showIntegration ? 480 : 340}}>
                {/* header row */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: gridCols,
                        pb: 1,
                        mb: 0.5,
                        borderBottom: `2px solid ${colors.neutral.grayLight}`,
                        alignItems: 'end'
                    }}
                >
                    <Typography
                        component="div"
                        variant="caption"
                        className="font-headers"
                        sx={{color: colors.text.secondary, textAlign: 'left'}}
                    >
                        Copilot Type
                    </Typography>
                    <Typography
                        component="div"
                        variant="caption"
                        className="font-headers"
                        sx={{...cellSx, color: colors.primary.navy, textAlign: 'left'}}
                    >
                        Development
                    </Typography>
                    {showIntegration && (
                        <Typography
                            component="div"
                            variant="caption"
                            className="font-headers"
                            sx={{...cellSx, color: colors.primary.navy, textAlign: 'left'}}
                        >
                            Integration
                        </Typography>
                    )}
                    <Typography
                        component="div"
                        variant="caption"
                        className="font-headers"
                        sx={{...cellSx, color: colors.text.secondary, textAlign: 'left'}}
                    >
                        Timeline
                    </Typography>
                </Box>

                {/* data rows */}
                {items?.map((item: any, idx: any) => (
                    <Box
                        key={idx}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: gridCols,
                            alignItems: 'center',
                            borderBottom: `1px dashed ${colors.neutral.grayLight}`,
                            py: 1.25
                        }}
                    >
                        <Typography component="div" className="font-headers" sx={{...firstCellSx}}>
                            {item.type ? parse(item.type) : ''}
                        </Typography>
                        <Typography component="div" className="font-headers" sx={{...cellSx, color: colors.primary.navy}}>
                            {item.cost ? parse(item.cost) : ''}
                        </Typography>
                        {showIntegration && (
                            <Typography component="div" className="font-headers" sx={{...cellSx, color: colors.primary.navy}}>
                                {item.integrationCost ? parse(item.integrationCost) : '—'}
                            </Typography>
                        )}
                        <Typography
                            component="div"
                            className="font-regular-text"
                            sx={{...cellSx, fontSize: fontSize[16], color: colors.text.primary}}
                        >
                            {item.timeline ? parse(item.timeline) : ''}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const renderTimelineBullets = (items: any) =>
    items?.map((item: any, idx: any) => (
        <Box key={idx} sx={bulletSx}>
            <Box sx={bulletPointSx} />
            <Typography
                className="font-regular-text"
                component="div"
                variant="body2"
                sx={{fontSize: fontSize[16], color: colors.text.primary}}
            >
                <Box className="font-headers">{item.label ? parse(item.label) : ''}:</Box>
                <Box className="font-regular-text">{item.time ? parse(item.time) : ''}</Box>
            </Typography>
        </Box>
    ));

const renderPlatformBullets = (items: any) =>
    items?.map((item: any, idx: any) => (
        <Box key={idx} sx={bulletSx}>
            <Box sx={bulletPointSx} />
            <Typography
                className="font-regular-text"
                component="div"
                variant="body2"
                sx={{fontSize: fontSize[16], color: colors.text.primary}}
            >
                <strong>{item.platform ? parse(item.platform) : ''}:</strong> {item.impact ? parse(item.impact) : ''}
            </Typography>
        </Box>
    ));

const renderAccordionContent = (accordion: any) => {
    const {type, introText, items, footer} = accordion;
    return (
        <AccordionDetails sx={{px: 0, pb: 0}}>
            <Box sx={{pb: 3}}>
                {introText && (
                    <Typography
                        component="div"
                        sx={{mb: 2, fontSize: fontSize[16], color: colors.text.primary}}
                        className="font-regular-text"
                    >
                        {introText}
                    </Typography>
                )}
                {type === 'bullets' && renderBullets(items as any)}
                {type === 'projectTable' && renderProjectTable(items as any)}
                {type === 'timelineBullets' && renderTimelineBullets(items as any)}
                {type === 'platformBullets' && renderPlatformBullets(items as any)}
            </Box>

            {footer && (
                <Box
                    sx={{
                        borderTop: `1px solid ${colors.neutral.grayLight}`,
                        pt: 2,
                        pb: 3,
                        color: colors.primary.navy,
                        fontStyle: 'italic'
                    }}
                >
                    <Typography
                        component="div"
                        variant="body2"
                        className="font-regular-text"
                        sx={{fontSize: fontSize[16], color: colors.text.primary}}
                    >
                        {parse(footer)}
                    </Typography>
                </Box>
            )}
        </AccordionDetails>
    );
};

// ---------- props ----------
interface CostItem {
    label: string;
    value: string;
    subtext?: string;
}

interface CostProps {
    heading?: string;
    subheading?: string;
    rangeText?: string;
    rangeSubtext?: string;
    rangeDescription?: string;
    rangeValue?: string;
    costItems?: CostItem[];
    accordions?: any;
}

// ---------- component ----------
const Cost = ({heading, subheading, rangeText, rangeSubtext, rangeDescription, rangeValue, costItems, accordions = []}: CostProps) => {
    const [expanded, setExpanded] = useState<number | false>(0);

    const handleChange = (panel: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers ">
                        {heading ? parse(heading) : ''}
                    </Typography>

                    <Typography
                        variant="subtitle1"
                        component="div"
                        className="font-regular-text remove-underline"
                        sx={{mb: {xs: 2, md: 6}, fontSize: fontSize[16], color: colors.text.primary}}
                    >
                        {subheading ? parse(subheading) : ''}
                    </Typography>
                </Box>

                <Box sx={mainCardSx}>
                    <Typography component="div" sx={impactHeaderSx} className="font-headers">
                        {rangeText ? parse(rangeText) : ''}
                    </Typography>

                    {costItems && costItems.length > 0 ? (
                        <Box sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 3, mb: 4}}>
                            {costItems.map((item, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        flex: 1,
                                        borderLeft: `3px solid ${colors.primary.navy}`,
                                        pl: 2,
                                        py: 0.5
                                    }}
                                >
                                    <Typography
                                        component="div"
                                        variant="caption"
                                        className="font-regular-text"
                                        sx={{
                                            fontSize: fontSize[16],
                                            color: colors.text.primary,
                                            mb: 0.5,
                                            display: 'block',
                                            textTransform: 'uppercase',
                                            letterSpacing: letterSpacing.wide
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                    <Typography
                                        component="div"
                                        className="font-headers"
                                        sx={{...impactValueSx, mb: item.subtext ? 0.5 : 0, fontSize: {xs: fontSize[28], md: fontSize[32]}}}
                                    >
                                        {parse(item.value)}
                                    </Typography>
                                    {item.subtext && (
                                        <Typography
                                            component="div"
                                            variant="caption"
                                            className="font-regular-text"
                                            sx={{fontSize: fontSize[16], color: colors.text.primary}}
                                        >
                                            {parse(item.subtext)}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <>
                            <Typography component="div" className="font-headers" sx={impactValueSx}>
                                {rangeValue ? parse(rangeValue) : ''}
                            </Typography>
                            {rangeDescription && (
                                <Typography
                                    component="div"
                                    sx={{mb: 2, fontSize: fontSize[16], color: colors.text.primary}}
                                    className="font-regular-text"
                                >
                                    {parse(rangeDescription)}
                                </Typography>
                            )}
                        </>
                    )}

                    <Typography component="div" sx={impactSubtextSx} className="font-regular-text">
                        {rangeSubtext ? parse(rangeSubtext) : ''}
                    </Typography>

                    {accordions?.map((accordion: any, idx: any) => (
                        <Accordion
                            key={idx}
                            sx={{...accordionSx, ...(idx === 0 && {borderTop: 'none'})}}
                            expanded={expanded === idx}
                            onChange={handleChange(idx)}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx} className="font-headers">
                                <Typography
                                    component="h3"
                                    sx={{fontWeight: fontWeight.bold, color: colors.text.primary, m: 0, fontSize: 'inherit'}}
                                >
                                    {accordion.title ? parse(accordion.title) : ''}
                                </Typography>
                            </AccordionSummary>

                            {renderAccordionContent(accordion)}
                        </Accordion>
                    ))}
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(Cost);
